import { useState } from "react";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCreateSwap, useEligibleSwapCandidates } from "@/hooks/schedule";
import type { EligibleCandidate } from "@/hooks/schedule";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
  DialogPortal,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Clock, MapPin, MessageSquare, X, Search, Users } from "lucide-react";

interface CreateSwapDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shift: {
    id: string;
    start_time: string;
    end_time: string;
    location?: string;
    note?: string;
  } | null;
  currentWeek?: Date; // NEW: Add currentWeek prop for cache invalidation
}

export function CreateSwapDialog({ open, onOpenChange, shift, currentWeek }: CreateSwapDialogProps) {
  const isMobile = useIsMobile();
  const [message, setMessage] = useState("");
  const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);

  const createSwap = useCreateSwap(currentWeek); // FIXED: Pass currentWeek for proper cache invalidation
  const { data: eligibleCandidates = [], isLoading: isLoadingCandidates } = useEligibleSwapCandidates(shift?.id);

  if (!shift) return null;

  const formatShiftTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
  };

  const formatShiftDate = (startTime: string) => {
    return format(new Date(startTime), 'EEEE, MMMM d, yyyy');
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleCandidateToggle = (candidateKey: string, checked: boolean) => {
    const newSelected = new Set(selectedCandidates);
    if (checked) {
      newSelected.add(candidateKey);
    } else {
      newSelected.delete(candidateKey);
    }
    setSelectedCandidates(newSelected);
  };

  const handleCreateSwap = async () => {
    if (selectedCandidates.size === 0) return;

    setIsCreating(true);
    try {
      // Convert selected candidates to the format expected by the API
      const candidateData = Array.from(selectedCandidates).map(candidateKey => {
        const candidate = eligibleCandidates.find(c => 
          `${c.profile_id}-${c.shift_id}` === candidateKey
        );
        if (!candidate) throw new Error('Invalid candidate selection');
        
        return {
          candidate_profile_id: candidate.profile_id,
          candidate_shift_id: candidate.shift_id,
        };
      });

      await createSwap.mutateAsync({
        requesterShiftId: shift.id,
        candidateData,
        message: message.trim() || undefined,
      });

      // Reset form and close dialog
      setMessage("");
      setSelectedCandidates(new Set());
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating swap:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setMessage("");
    setSelectedCandidates(new Set());
    onOpenChange(false);
  };

  // Group candidates by profile
  const candidatesByProfile = eligibleCandidates.reduce((acc, candidate) => {
    if (!acc[candidate.profile_id]) {
      acc[candidate.profile_id] = {
        profile_name: candidate.profile_name,
        shifts: []
      };
    }
    acc[candidate.profile_id].shifts.push(candidate);
    return acc;
  }, {} as Record<string, { profile_name: string; shifts: EligibleCandidate[] }>);

  // Shared content component
  const content = (
    <div className="space-y-6">
      {/* Your shift info */}
      <div>
        <h4 className="text-sm font-medium text-muted-foreground mb-2">
          Your shift to swap:
        </h4>
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h5 className="font-medium text-foreground">
                  {formatShiftDate(shift.start_time)}
                </h5>
                <Badge variant="outline" className="text-orange-700 border-orange-300">
                  Your Shift
                </Badge>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {formatShiftTime(shift.start_time, shift.end_time)}
                </div>
                {shift.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {shift.location}
                  </div>
                )}
              </div>
              {shift.note && (
                <p className="text-sm text-muted-foreground mt-2">
                  Note: {shift.note}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Message input */}
      <div className="space-y-2">
        <Label htmlFor="swap-message" className="text-sm font-medium">
          Message (optional)
        </Label>
        <Textarea
          id="swap-message"
          placeholder="Would you like to swap shifts with me?"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="min-h-[80px] resize-none"
          maxLength={500}
        />
        <p className="text-xs text-muted-foreground">
          {message.length}/500 characters
        </p>
      </div>

      {/* Available candidates */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium text-foreground">
            Available shifts to swap with:
          </h4>
        </div>

        {isLoadingCandidates ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading available shifts...</div>
          </div>
        ) : Object.keys(candidatesByProfile).length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-6 text-center">
              <Search className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No available shifts found for swapping.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Other team members may not have future shifts scheduled yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <ScrollArea className="max-h-[300px] pr-4">
            <div className="space-y-3">
              {Object.entries(candidatesByProfile).map(([profileId, profileData]) => (
                <div key={profileId} className="space-y-2">
                  {/* Profile header */}
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                        {getInitials(profileData.profile_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-foreground">
                      {profileData.profile_name}
                    </span>
                  </div>

                  {/* Profile's shifts */}
                  <div className="ml-8 space-y-2">
                    {profileData.shifts.map((candidate) => {
                      const candidateKey = `${candidate.profile_id}-${candidate.shift_id}`;
                      const isSelected = selectedCandidates.has(candidateKey);

                      return (
                        <Card 
                          key={candidateKey}
                          className={`cursor-pointer transition-colors ${
                            isSelected 
                              ? 'border-blue-300 bg-blue-50/50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => handleCandidateToggle(candidateKey, !isSelected)}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(checked) => handleCandidateToggle(candidateKey, !!checked)}
                                className="flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium text-foreground">
                                    {formatShiftDate(candidate.shift_start_time)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatShiftTime(candidate.shift_start_time, candidate.shift_end_time)}
                                  </div>
                                </div>
                                {candidate.shift_note && (
                                  <p className="text-xs text-muted-foreground mt-1 truncate">
                                    Note: {candidate.shift_note}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      {selectedCandidates.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>{selectedCandidates.size}</strong> shift{selectedCandidates.size === 1 ? '' : 's'} selected for swap request
          </p>
        </div>
      )}
    </div>
  );

  // Shared button group
  const buttonGroup = (
    <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-2">
      <Button 
        variant="outline" 
        onClick={handleClose}
        disabled={isCreating}
      >
        Cancel
      </Button>
      <Button 
        onClick={handleCreateSwap}
        disabled={isCreating || selectedCandidates.size === 0}
        className="bg-blue-600 hover:bg-blue-700"
      >
        {isCreating ? "Sending..." : `Send Swap Request${selectedCandidates.size > 1 ? 's' : ''}`}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="z-[60]">
          <DrawerHeader className="text-left">
            <div className="flex items-center justify-between">
              <DrawerTitle>Swap Shift</DrawerTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </DrawerHeader>
          <div className="px-4 pb-4">
            {content}
          </div>
          <DrawerFooter className="px-4">
            {buttonGroup}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
        <DialogOverlay className="fixed inset-0 z-[60] bg-black/50" />
        <DialogContent className="z-[60] sm:max-w-[700px] max-h-[90vh]" aria-describedby="create-swap-description">
          <DialogHeader>
            <DialogTitle>Swap Shift</DialogTitle>
          </DialogHeader>
          <div id="create-swap-description" className="sr-only">
            Dialog for creating a new shift swap request by selecting available shifts from other team members.
          </div>
          <ScrollArea className="max-h-[calc(90vh-200px)]">
            {content}
          </ScrollArea>
          <DialogFooter>
            {buttonGroup}
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
